import type { ComponentProps } from 'react'
import ShapeManager from '@/components/app/shapes/shape-manager'
import { useStableCallbacks } from '@/hooks/use-stable-callbacks'
import { EMPTY_TEXT_CHAINS } from '../constants/empty-text-chains'
import { handleLinkedTextExcludedFromGroup } from '../handlers/linked-text-excluded-from-group'
import type {
  CanvasEditModeHandlers,
  CanvasEditModes,
  CanvasFoundation,
  CanvasPointerGestures,
  CanvasSelectionCommands,
  CanvasTextLinking,
  CanvasToolModes,
  CanvasViewport,
} from '../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../types/resolved-center-canvas-config'

type ShapeManagerProps = ComponentProps<typeof ShapeManager>
type ParamsOf<K extends keyof ShapeManagerProps> =
  NonNullable<ShapeManagerProps[K]> extends (...args: infer P) => unknown ? P : never

interface CanvasContentLayerProps {
  config: Pick<
    ResolvedCenterCanvasConfig,
    | 'elementZIndexMap'
    | 'onShapeClick'
    | 'onUserEdit'
    | 'onShapeCreated'
    | 'showTextLinking'
    | 'linkedTextChains'
    | 'onChainBackspaceAtStart'
    | 'onChainArrowLeftAtStart'
    | 'onChainArrowRightAtEnd'
    | 'onShapeChainOverflowCaret'
    | 'onShapeTextLiveEdit'
    | 'onShapeDeleted'
    | 'snapToGuides'
    | 'snapGuideDistance'
    | 'snapToGrid'
    | 'unitOfMeasure'
    | 'onSelectionChange'
    | 'onCornerResizeGestureActiveChange'
    | 'onLiveWrapChainSync'
    | 'onShapeTextEditStateChange'
    | 'onExpandTypePanel'
    | 'isTextToolActive'
    | 'onActivateSelectTool'
  >
  foundation: Pick<
    CanvasFoundation,
    | 'isInlineTextEditing'
    | 'isShapeTextEditing'
    | 'setIsShapeTextEditing'
    | 'setShapeManagerRef'
    | 'newShapeTextSeedStyle'
    | 'contextMenus'
    | 'getLinkedTextChainsForShapeManager'
    | 'lastShapeSelectionClearKeyRef'
    | 'setSelectedGuideIds'
    | 'isPanInteractionActive'
    | 'effectiveSmartGuides'
    | 'setActiveSmartGuides'
  >
  editModes: Pick<
    CanvasEditModes,
    | 'isCanvasSelectionEnabled'
    | 'textOnPathDraft'
    | 'textOnPathDraftRef'
    | 'handleTextOnPathLengthChange'
    | 'handleTextOnPathDotDrag'
    | 'commitAndExitTextOnPathMode'
    | 'selectedShapeIdsForUnified'
    | 'setSelectedShapeIdsForUnified'
    | 'handleShapeSelectionHistoryFromShapeManager'
    | 'isNodeEditMode'
    | 'isCropMode'
    | 'activeIsolationGroupId'
    | 'groupIsolationPath'
    | 'exitGroupIsolationMode'
    | 'unlockSelectedObjects'
    | 'unfreezeSelectedObjects'
  >
  linking: Pick<
    CanvasTextLinking,
    'isTextLinkingActive' | 'setTextLinkingRevision' | 'handleSelectChainMembers'
  >
  toolModes: CanvasToolModes
  viewport: Pick<CanvasViewport, 'scale' | 'documentGridSnapStepPt' | 'gridSnapLiveRects'>
  pointer: Pick<CanvasPointerGestures, 'snapGuides'>
  selectionCommands: Pick<CanvasSelectionCommands, 'handleShapeDoubleClick'>
  editModeHandlers: Pick<
    CanvasEditModeHandlers,
    'handleShapeCommitted' | 'handleBatchShapesCommitted' | 'handleLiveSelectionGeometrySync'
  >
}

/**
 * The unified content layer: the ShapeManager that renders and edits every shape and text frame,
 * wired to the canvas's modes, snapping, linking, selection and text-edit callbacks.
 *
 * `ShapeManager` is memoized, and this layer re-renders on every `CenterCanvas` render (including
 * once per drag frame while the text-linking overlay tracks geometry), so every callback it gets
 * goes through `useStableCallbacks`: the shapes only re-render when their own data changes.
 */
export function CanvasContentLayer({
  config,
  foundation,
  editModes,
  linking,
  toolModes,
  viewport,
  pointer,
  selectionCommands,
  editModeHandlers,
}: CanvasContentLayerProps) {
  const {
    isInlineTextEditing,
    isShapeTextEditing,
    setIsShapeTextEditing,
    contextMenus,
    lastShapeSelectionClearKeyRef,
    setSelectedGuideIds,
  } = foundation
  const {
    isCanvasSelectionEnabled,
    textOnPathDraftRef,
    commitAndExitTextOnPathMode,
    selectedShapeIdsForUnified,
    setSelectedShapeIdsForUnified,
    isNodeEditMode,
    isCropMode,
  } = editModes
  const { isTextLinkingActive, setTextLinkingRevision } = linking
  const {
    showTextLinking,
    onShapeClick,
    onSelectionChange,
    onLiveWrapChainSync,
    onShapeTextEditStateChange,
    onExpandTypePanel,
    onActivateSelectTool,
    isTextToolActive,
  } = config

  const callbacks = useStableCallbacks({
    onShapeClick: (shapeId: string, isToggle?: boolean) => {
      onShapeClick?.(shapeId, isToggle)
    },
    onShapeDoubleClick: selectionCommands.handleShapeDoubleClick,
    onShapeContextMenu: contextMenus.handleShapeContextMenu,
    onTextOnPathLengthChange: editModes.handleTextOnPathLengthChange,
    onTextOnPathStartDistanceChange: editModes.handleTextOnPathDotDrag,
    onUserEdit: config.onUserEdit,
    onShapeCreated: config.onShapeCreated,
    onShapeCommitted: (...[shape, source, undo]: ParamsOf<'onShapeCommitted'>) => {
      // Keep link ports/connectors on a converted shape text frame in step with
      // its committed geometry, mirroring `onTextBoxCommitted` (WMPCM-7988).
      if (showTextLinking || isTextLinkingActive || shape.uid != null) {
        setTextLinkingRevision((r) => r + 1)
      }

      editModeHandlers.handleShapeCommitted(shape, source, undo)
    },
    onBatchShapesCommitted: editModeHandlers.handleBatchShapesCommitted,
    getLinkedTextChains: foundation.getLinkedTextChainsForShapeManager,
    onSelectShapeTextChain: linking.handleSelectChainMembers,
    onShapeTextChainBackspaceAtStart: config.onChainBackspaceAtStart,
    onShapeTextChainArrowLeftAtStart: config.onChainArrowLeftAtStart,
    onShapeTextChainArrowRightAtEnd: config.onChainArrowRightAtEnd,
    onShapeTextChainOverflowCaret: config.onShapeChainOverflowCaret,
    onShapeTextLiveEdit: config.onShapeTextLiveEdit,
    onShapeDeleted: config.onShapeDeleted,
    onSelectionChange: (...[ids, meta]: ParamsOf<'onSelectionChange'>) => {
      // Dismiss-to-canvas (AC-11 semantics): the bar commits the moment the
      // selection leaves the object it is editing — clicking empty canvas, or
      // clicking a different object. `tryEnterTextOnPathMode` selects its
      // target, so this can only fire on a genuine move away.
      const editingId = textOnPathDraftRef.current?.shapeId

      if (editingId != null && !ids.includes(editingId)) {
        commitAndExitTextOnPathMode()
      }

      if (ids.length > 0) {
        const key = [...ids].sort().join('\u0000')

        if (lastShapeSelectionClearKeyRef.current !== key) {
          lastShapeSelectionClearKeyRef.current = key
          setSelectedGuideIds(new Set())
        }
      } else {
        lastShapeSelectionClearKeyRef.current = ''
      }

      setSelectedShapeIdsForUnified(ids)
      onSelectionChange?.(ids, meta)
    },
    onShapeSelectionHistory: editModes.handleShapeSelectionHistoryFromShapeManager,
    onExitGroupIsolationMode: editModes.exitGroupIsolationMode,
    onActiveSmartGuidesChange: foundation.setActiveSmartGuides,
    onLiveSelectionGeometrySync: editModeHandlers.handleLiveSelectionGeometrySync,
    onCornerResizeGestureActiveChange: config.onCornerResizeGestureActiveChange,
    onLiveShapeGeometryChangeForWrap: () => {
      // A shape converted to a text frame carries link ports, so the overlay
      // must re-read its geometry mid-gesture or the ports lag behind the
      // shape until pointer-up (WMPCM-7988).
      if (showTextLinking || isTextLinkingActive || selectedShapeIdsForUnified.length > 0) {
        setTextLinkingRevision((r) => r + 1)
      }

      onLiveWrapChainSync?.()
    },
    onUnlockSelectionBadgeClick: editModes.unlockSelectedObjects,
    onUnfreezeSelectionBadgeClick: editModes.unfreezeSelectedObjects,
    onShapeTextContextMenu: contextMenus.handleShapeTextContextMenu,
    onShapeTextEditEnter: () => {
      setIsShapeTextEditing(true)
      onShapeTextEditStateChange?.()
      onExpandTypePanel?.()
    },
    onShapeTextEditExit: (...[opts]: ParamsOf<'onShapeTextEditExit'>) => {
      setIsShapeTextEditing(false)
      onShapeTextEditStateChange?.()
      // Esc / explicit exit → Select; blur while switching editors stays on Type.
      if (opts?.activateSelectTool || !isTextToolActive) {
        onActivateSelectTool?.()
      }
    },
  })

  return (
    // Text Manager (above shapes in z-order)
    <div
      className="absolute inset-0"
      style={{
        pointerEvents: 'none',
        zIndex: isInlineTextEditing || isShapeTextEditing ? 45 : 2,
      }}
    >
      {/* Shape Manager */}
      <div
        className="absolute inset-0"
        style={{
          pointerEvents: isCanvasSelectionEnabled ? 'auto' : 'none',
        }}
      >
        <ShapeManager
          ref={foundation.setShapeManagerRef}
          defaultShapeTextSeedStyle={foundation.newShapeTextSeedStyle}
          className="absolute inset-0"
          elementZIndexMap={config.elementZIndexMap}
          onShapeClick={callbacks.onShapeClick}
          onShapeDoubleClick={callbacks.onShapeDoubleClick}
          onShapeContextMenu={callbacks.onShapeContextMenu}
          textOnPathPreview={editModes.textOnPathDraft}
          onTextOnPathLengthChange={callbacks.onTextOnPathLengthChange}
          onTextOnPathStartDistanceChange={callbacks.onTextOnPathStartDistanceChange}
          onUserEdit={callbacks.onUserEdit}
          onShapeCreated={callbacks.onShapeCreated}
          onShapeCommitted={callbacks.onShapeCommitted}
          onBatchShapesCommitted={callbacks.onBatchShapesCommitted}
          // Both, deliberately (WMPCM-6867). The PROP is what re-renders
          // ShapeManager when a chain changes; the GETTER is for its imperative reads,
          // where a stable identity matters. Removing either one reintroduces a bug:
          // without the prop a link/unlink that coincides with no other state change
          // never repaints, without the getter every chain edit rebuilds the group
          // adoption callback (WMPCM-6948).
          linkedTextChains={config.linkedTextChains ?? EMPTY_TEXT_CHAINS}
          getLinkedTextChains={callbacks.getLinkedTextChains}
          onSelectShapeTextChain={callbacks.onSelectShapeTextChain}
          // The same three handlers TextManager gets — they resolve both sides of the
          // boundary through `collectLinkableFrames`, so either end can be a shape.
          onShapeTextChainBackspaceAtStart={callbacks.onShapeTextChainBackspaceAtStart}
          onShapeTextChainArrowLeftAtStart={callbacks.onShapeTextChainArrowLeftAtStart}
          onShapeTextChainArrowRightAtEnd={callbacks.onShapeTextChainArrowRightAtEnd}
          onShapeTextChainOverflowCaret={callbacks.onShapeTextChainOverflowCaret}
          onShapeTextLiveEdit={callbacks.onShapeTextLiveEdit}
          onLinkedTextExcludedFromGroup={handleLinkedTextExcludedFromGroup}
          onShapeDeleted={callbacks.onShapeDeleted}
          guides={pointer.snapGuides}
          snapToGuides={config.snapToGuides}
          snapGuideDistance={config.snapGuideDistance}
          snapToGrid={config.snapToGrid}
          gridSnapStep={viewport.documentGridSnapStepPt}
          gridSnapLiveRects={viewport.gridSnapLiveRects}
          unitOfMeasure={config.unitOfMeasure}
          onSelectionChange={callbacks.onSelectionChange}
          onShapeSelectionHistory={callbacks.onShapeSelectionHistory}
          scale={viewport.scale}
          isInShapeCreationMode={
            toolModes.isInShapeCreationMode ||
            toolModes.isInPenToolMode ||
            isNodeEditMode ||
            isCropMode ||
            toolModes.isInTextCreationMode ||
            foundation.isPanInteractionActive ||
            isTextLinkingActive
          }
          isSelectionEnabled={isCanvasSelectionEnabled}
          editScopeGroupId={editModes.activeIsolationGroupId}
          isolationRootGroupId={editModes.groupIsolationPath[0] ?? null}
          onExitGroupIsolationMode={callbacks.onExitGroupIsolationMode}
          isInNodeEditMode={isNodeEditMode}
          smartGuidesEnabled={foundation.effectiveSmartGuides}
          onActiveSmartGuidesChange={callbacks.onActiveSmartGuidesChange}
          onLiveSelectionGeometrySync={callbacks.onLiveSelectionGeometrySync}
          onCornerResizeGestureActiveChange={callbacks.onCornerResizeGestureActiveChange}
          onLiveShapeGeometryChangeForWrap={callbacks.onLiveShapeGeometryChangeForWrap}
          onUnlockSelectionBadgeClick={callbacks.onUnlockSelectionBadgeClick}
          onShapeTextContextMenu={callbacks.onShapeTextContextMenu}
          suppressShapeTextExitOnBlur={contextMenus.shapeTextEditContextMenu.open}
          onShapeTextEditEnter={callbacks.onShapeTextEditEnter}
          onShapeTextEditExit={callbacks.onShapeTextEditExit}
          onUnfreezeSelectionBadgeClick={callbacks.onUnfreezeSelectionBadgeClick}
        />
      </div>
    </div>
  )
}
